import {
  DealFirmQuery,
  useRemoveDealAccountMutation,
  useDealAccountQuery,
  useDealActivityQuery,
  DealAccountQuery,
  DealAccessStatus,
  useUpdateDealAccountMutation,
  useTrackActivityMutation,
  DealActivityType,
  DealGroup,
} from "../../graphql/generated";
import { AnimatedModal } from "../../components/AnimatedModal";
import { CloseIcon } from "../../components/CloseIcon";
import { useQueryClient } from "@tanstack/react-query";
import useGqlClient from "../../hooks/useGqlClient";
import { useHistory, useParams } from "react-router-dom";
import Loading from "@/src/components/Loading";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleMinusIcon,
  EyeIcon,
  PencilIcon,
} from "lucide-react";
import { Avatar } from "@/src/components/account/Avatar";
import { Button } from "@/src/components/tailwind/Button";
import { formatDistanceToNow, fromUnixTime } from "date-fns";
import { DataRoomPermissions } from "./AccessList";
import { FileIcon } from "@/src/components/FileIcon";
import { classNames } from "@/src/utils/cn";
import { FolderIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import { ActivityItems } from "@/src/components/activity/ActivityItems";
import { useDispatch, useSelector } from "react-redux";
import { authSelectors } from "@/src/store/auth/selector";
import { toasts } from "@/src/components/toasts/toasts";
import { actions } from "@/src/store/auth/slice";
import {
  buildFolderHierarchy,
  FolderWrapper,
} from "@/src/utils/buildFolderHierarchy";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";
import { truncateText } from "@/src/utils/truncateText";
import { BackButton } from "@/src/components/BackButton";
import { EditDealAccount } from "./EditDealAccount";
import { DealPermissionsSummary } from "@/src/components/permissions/DealPermissionsSummary";

export function DealAccountDetails() {
  const { dealAccountId } = useParams<{ dealAccountId: string }>();
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const history = useHistory();
  const { dealId } = useParams<{ dealId: string }>();

  const [openModal, setOpenModal] = useState<"edit_deal_account" | "">("");

  const dealAccountQuery = useDealAccountQuery(client, {
    id: dealAccountId,
    dealId,
  });

  const { invalidatePermissionChecks } = useInvalidateQueryKeys();

  const account = useSelector(authSelectors.account);
  const trackActivity = useTrackActivityMutation(client);

  const dispatch = useDispatch();

  const [fileSearch, setFileSearch] = useState("");

  if (dealAccountQuery.error) {
    return (
      <div className="flex flex-1 justify-center items-center">
        <p className="text-gray-500 font-semibold">Something went wrong</p>
      </div>
    );
  }

  if (dealAccountQuery.isLoading || !dealAccountQuery.data) {
    return (
      <div className="flex flex-1 justify-center items-center">
        <Loading />
      </div>
    );
  }

  const dealAccount = dealAccountQuery.data.dealAccount;
  const folders = buildFolderHierarchy(dealAccount.allFolders);
  return (
    <div className="w-full">
      <div className="px-3 flex items-center gap-x-2 w-full border-y border-concrete-100 py-3">
        <BackButton onClick={() => history.goBack()} size="sm" />
        <p className="text-md leading-tight text-persian-990 font-semibold">
          {dealAccount.account.name}
        </p>
      </div>
      <div className="flex flex-col divide-x divide-concrete-100 lg:flex-row lg:gap-x-12">
        <div className="py-4 mx-3 min-w-0 lg:w-80 lg:flex-none">
          <div className="flex items-center gap-x-3">
            <Avatar account={dealAccount.account} size="m" />
            <div>
              <p className="text-md leading-tight text-persian-990 font-semibold">
                {dealAccount.account.name}
              </p>
              <p className="text-sm text-concrete-800">
                {dealAccount.account.email}
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2">
            <p className="text-sm text-persian-990">Group</p>
            <p className="text-sm text-persian-990 font-semibold">
              {dealAccount.dealGroup ? dealAccount.dealGroup.name : "-"}
            </p>

            <p className="text-sm text-persian-990">Access</p>
            <DataRoomPermissions
              restrictedAccess={
                dealAccount.dealGroup
                  ? dealAccount.dealGroup.overrideDataRoomAccess
                  : dealAccount.overrideDataRoomAccess
              }
              granularAccess={
                dealAccount.dealGroup
                  ? dealAccount.dealGroup.granularAccess
                  : dealAccount.granularAccess
              }
              permissions={
                dealAccount.dealGroup
                  ? dealAccount.dealGroup.dataRoomPermissions
                  : dealAccount.dataRoomPermissions
              }
              prefixText=""
            />
            {/* <p className="text-sm text-gray-600 font-semibold">
              {formatDataRoomAccess(
                dealAccount.overrideDataRoomAccess,
                dealAccount.granularAccess,
                dealAccount.dataRoomPermission,
                dealAccount.dataRoomPermissions,
                dealAccount.dealGroup as DealGroup,
                dealAccount.dealAccessStatus,
              )}
            </p> */}
            <p className="text-sm text-persian-990">Role</p>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-persian-990">
                {dealAccount.dealRole.name}
              </p>
              <DealPermissionsSummary
                className="mt-1.5 w-full"
                permissions={dealAccount.dealRole.permissionLevels}
                roleType={dealAccount.dealRole.type}
              />
            </div>
            <p className="text-sm text-persian-990">Last seen</p>
            <p className="text-sm text-persian-990 font-semibold">
              {formatDistanceToNow(fromUnixTime(dealAccount.lastSeen), {
                addSuffix: true,
              })}
            </p>
          </div>

          <div className="mt-4 flex flex-col gap-y-2">
            <Button
              isDisabled={account?.id === dealAccount.account.id}
              text={`View deal as ${dealAccount.account.name}`}
              variant="positive"
              icon={EyeIcon}
              onClick={() => {
                dispatch(
                  actions.setActor({
                    actor: {
                      account: dealAccount.account,
                      dealAccountId: dealAccount.id,
                    },
                  }),
                );
                setTimeout(() => {
                  invalidatePermissionChecks();
                  history.push(`/deal/${dealId}/dashboard`);
                }, 200);
                trackActivity.mutate({
                  type: DealActivityType.AccountImpersonation,
                  targetAccountID: dealAccount.account.id,
                });
              }}
            />
            <Button
              isDisabled={account?.id === dealAccount.account.id}
              variant="secondary"
              text="Edit user"
              icon={PencilIcon}
              onClick={() => {
                setOpenModal("edit_deal_account");
              }}
            />
            <AccessStatusButton
              dealAccount={dealAccount}
              isDisabled={account?.id === dealAccount.account.id}
            />
          </div>
        </div>

        <div className="flex-1 mt-8 lg:mt-0 lg:pt-4">
          <div>
            <div className="mx-3">
              <p className="text-persian-990 text-md flex-1 font-semibold">
                {dealAccount.account.name} can access
              </p>

              <input
                className="mt-3 mb-2 focus:ring-0 rounded-md text-sm border-concrete-100 w-80 focus:border-concrete-700"
                placeholder="Search..."
                value={fileSearch}
                onChange={(e) => setFileSearch(e.target.value)}
              />
            </div>

            <div className="overflow-y-auto max-h-[400px]">
              {folders.map((folder) => (
                <FolderContent
                  folder={folder}
                  searchTerm={fileSearch}
                  depth={0}
                />
              ))}
            </div>
          </div>
          <div className="mt-5 mx-3">
            <p className="mb-2 text-gray-700 font-semibold">Activity</p>
            <DealActivity dealId={dealId} accountId={dealAccount.account.id} />
          </div>
        </div>
      </div>
      <EditDealAccount
        dealAccount={dealAccount}
        dealId={dealId}
        groups={dealAccountQuery.data.deal.allGroups as DealGroup[]}
        onClose={() => {
          setOpenModal("");
          queryClient.invalidateQueries({
            queryKey: ["DealAccount", { dealId: dealId, id: dealAccountId }],
          });
        }}
        open={openModal === "edit_deal_account"}
        permissions={dealAccountQuery.data.deal.dataRoom.permissions}
        dealGroup={dealAccount.dealGroup as DealGroup}
      />
    </div>
  );
}

function DealActivity(props: { dealId: string; accountId: string }) {
  const client = useGqlClient();
  const dealActivityQuery = useDealActivityQuery(client, {
    dealId: props.dealId,
    input: {
      accountID: props.accountId,
    },
  });

  if (dealActivityQuery.error) {
    return (
      <div className="flex flex-1 justify-center items-center">
        <p className="text-gray-500 font-semibold">Something went wrong</p>
      </div>
    );
  }

  if (dealActivityQuery.isLoading || !dealActivityQuery.data) {
    return (
      <div className="flex flex-1 justify-center items-center">
        <Loading />
      </div>
    );
  }

  if (dealActivityQuery.data.dealActivity.activity.length === 0) {
    return (
      <div className="p-3 flex flex-col justify-center items-center flex-1">
        <p className="text-gray-500/80 text-sm font-semibold">
          No account activity yet
        </p>
      </div>
    );
  }

  return (
    <div>
      <ActivityItems items={dealActivityQuery.data.dealActivity.activity} />
    </div>
  );
}

function RemoveDealAccount(props: {
  dealAccount: DealFirmQuery["dealFirm"]["dealAccounts"][0];
  dealFirmId: string;
  dealFirmGroupId: string;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const client = useGqlClient();
  const removeDealAccount = useRemoveDealAccountMutation(client);

  return (
    <AnimatedModal open={props.open} onClose={props.onClose}>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">
          Remove {props.dealAccount.account.name} from the deal
        </h2>
        <CloseIcon onClose={props.onClose} />
      </div>
      <p className="text-sm text-gray-600 mt-2">
        Are you sure you want to remove this user from the deal?
      </p>
      <div className="flex justify-end mt-4">
        <button
          type="button"
          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
          onClick={() => {
            props.onClose();
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
          onClick={() => {
            removeDealAccount.mutate(
              {
                id: props.dealAccount.id,
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: ["DealFirm", { id: props.dealFirmId }],
                  });
                  queryClient.invalidateQueries({
                    queryKey: ["DealFirmGroup", { id: props.dealFirmGroupId }],
                  });
                  props.onClose();
                },
              },
            );
          }}
        >
          Remove
        </button>
      </div>
    </AnimatedModal>
  );
}

function FolderContent(props: {
  folder: FolderWrapper;
  searchTerm: string;
  depth: number;
}) {
  const [showChildren, setShowChildren] = useState(true);

  if (props.searchTerm.length > 2) {
    if (
      !props.folder.folder.name
        .toLowerCase()
        .includes(props.searchTerm.toLowerCase()) &&
      props.folder.files.filter((file) =>
        file.name.toLowerCase().includes(props.searchTerm.toLowerCase()),
      ).length === 0
    ) {
      return null;
    }
  }

  return (
    <div className="">
      <div
        onClick={() => {
          setShowChildren(!showChildren);
        }}
        className={classNames(
          "items-center hover:bg-concrete-50 px-3 flex py-1 justify-between cursor-pointer",
        )}
        style={{ paddingLeft: `${props.depth * 0.8}rem` }}
      >
        <div className="flex px-3 items-center gap-x-1">
          {showChildren ? (
            <ChevronDownIcon className="w-4 h-4 text-concrete-600" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-concrete-600" />
          )}
          <FolderIcon className="w-5 h-5 text-blue-700/70" />
          <p
            className="font-semibold mt-1 ml-1 select-none text-persian-990 text-sm"
            title={props.folder.folder.name}
          >
            {props.folder.folder.name
              ? truncateText(props.folder.folder.name, 100)
              : "Home"}
          </p>
        </div>
      </div>
      {showChildren ? (
        <div className="">
          <div>
            {props.folder.folders.map((folder) => (
              <FolderContent
                folder={folder}
                searchTerm={props.searchTerm}
                depth={props.depth + 1}
                key={folder.folder.id}
              />
            ))}
          </div>
          <div>
            {props.folder.files
              .filter((file) =>
                props.searchTerm.length > 2
                  ? file.name
                    .toLowerCase()
                    .includes(props.searchTerm.toLowerCase())
                  : true,
              )
              .map((file) => {
                return (
                  <div
                    key={file.id}
                    className={`cursor-pointer pl-6 pr-3 justify-between hover:bg-gray-200/70  items-center flex  py-2`}
                    style={{
                      paddingLeft: `${(props.depth + 1) * 0.8}rem`,
                    }}
                  >
                    <div className="ml-2 flex items-center">
                      <FileIcon fileType={file.fileType} />
                      <p className="ml-1 select-none font-semibold text-sm text-gray-600">
                        {file.name}
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AccessStatusButton(props: {
  dealAccount: DealAccountQuery["dealAccount"];
  isDisabled: boolean;
}) {
  const dealAccount = props.dealAccount;
  const dealGroup = dealAccount.dealGroup;
  const client = useGqlClient();
  const updateDealAccount = useUpdateDealAccountMutation(client);
  const queryClient = useQueryClient();

  function updateDealAccountFn(status: DealAccessStatus) {
    updateDealAccount.mutate(
      {
        input: {
          id: dealAccount.id,
          dealAccessStatus: status,
          overrideDataRoomAccess: dealAccount.overrideDataRoomAccess,
          filePermissions: dealAccount.granularAccess?.files.map((f) => ({
            fileId: f.id,
            type: f.type,
          })),
          folderPermissions: dealAccount.granularAccess?.folders.map((f) => ({
            folderId: f.id,
            type: f.type,
          })),
          roleID: dealAccount.dealRole.id,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["DealAccount", { id: dealAccount.id }],
          });
          toasts.success("Account access updated");
        },
        onError: () => {
          toasts.error("Failed to update account access");
        },
      },
    );
  }

  if (dealAccount.dealAccessStatus === DealAccessStatus.PendingManualApproval) {
    return (
      <Button
        isDisabled={props.isDisabled}
        text="Approve account"
        variant="green"
        icon={CheckCircleIcon}
        onClick={() => {
          updateDealAccountFn(DealAccessStatus.Granted);
        }}
      />
    );
  }

  if (
    dealAccount.dealAccessStatus === DealAccessStatus.Blocked ||
    (dealGroup && dealGroup.dealAccessStatus === DealAccessStatus.Blocked)
  ) {
    return (
      <Button
        isDisabled={props.isDisabled}
        text="Unblock account"
        variant="green"
        icon={CheckCircleIcon}
        onClick={() => {
          updateDealAccountFn(DealAccessStatus.Granted);
        }}
      />
    );
  }

  return (
    <Button
      isDisabled={props.isDisabled}
      text="Block account"
      variant="danger"
      icon={CircleMinusIcon}
      onClick={() => {
        updateDealAccountFn(DealAccessStatus.Blocked);
      }}
    />
  );
}
